/** `/create` — host onboarding: name, character, host-is-player toggle, voice mode, then POST /api/sessions. */
import { useState, type FormEvent } from 'react';
import { CHARACTER_SLOTS, CHARACTER_SLOT_IDS, type CharacterSlotId, type VoiceMode } from '@momentum/shared';
import { ApiError, createSession } from '../api.ts';
import { HOST_SESSION_KEY, saveCredentials } from '../session/credentials.ts';
import { useNavigate } from '../shell/router.tsx';
import { CharacterBadge } from '../components/CharacterBadge.tsx';
import { strings } from '../shell/strings.ts';

const VOICE_MODE_OPTIONS: { value: VoiceMode; label: string; hint: string }[] = [
  { value: 'remote', label: strings.voice.modeRemote, hint: strings.voice.modeRemoteHint },
  { value: 'in_person', label: strings.voice.modeInPerson, hint: strings.voice.modeInPersonHint },
  { value: 'off', label: strings.voice.modeOff, hint: strings.voice.modeOffHint },
];

export function CreateSession() {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [characterSlot, setCharacterSlot] = useState<CharacterSlotId>(CHARACTER_SLOT_IDS[0]);
  const [hostIsPlayer, setHostIsPlayer] = useState(true);
  const [voiceMode, setVoiceMode] = useState<VoiceMode>('remote');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await createSession({
        displayName,
        characterSlot,
        hostIsPlayer,
        settings: { voiceMode },
      });
      saveCredentials(response.sessionId, response.participant);
      try { localStorage.setItem(HOST_SESSION_KEY, response.sessionId); } catch { /* Browser storage may be unavailable. */ }
      navigate(`/s/${response.sessionId}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : strings.common.error);
      setSubmitting(false);
    }
  }

  return (
    <div className="page stack">
      <h1>{strings.create.title}</h1>
      <form className="stack" onSubmit={submit}>
        <div className="field">
          <label htmlFor="create-name">{strings.create.displayNameLabel}</label>
          <input
            id="create-name"
            type="text"
            required
            maxLength={32}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={strings.create.displayNamePlaceholder}
          />
        </div>

        <fieldset className="stack-sm" style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend>{strings.create.characterLabel}</legend>
          <div className="row">
            {CHARACTER_SLOT_IDS.map((slot) => (
              <button
                key={slot}
                type="button"
                className={characterSlot === slot ? 'button card-selected' : 'button'}
                aria-pressed={characterSlot === slot}
                onClick={() => setCharacterSlot(slot)}
              >
                <CharacterBadge slot={slot} displayName={CHARACTER_SLOTS[slot].label} size={32} />
              </button>
            ))}
          </div>
        </fieldset>

        <div>
          <label className="choice-row">
            <input type="checkbox" checked={hostIsPlayer} onChange={(e) => setHostIsPlayer(e.target.checked)} />
            {strings.create.hostIsPlayerLabel}
          </label>
          <p className="text-muted">{strings.create.hostIsPlayerHint}</p>
        </div>

        <fieldset className="stack-sm" style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend>{strings.create.voiceModeLabel}</legend>
          {VOICE_MODE_OPTIONS.map((option) => (
            <div key={option.value}>
              <label className="choice-row">
                <input
                  type="radio"
                  name="voice-mode"
                  checked={voiceMode === option.value}
                  onChange={() => setVoiceMode(option.value)}
                />
                {option.label}
              </label>
              <p className="text-muted">{option.hint}</p>
            </div>
          ))}
        </fieldset>

        {error ? (
          <p className="badge badge-danger" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" className="button button-primary" disabled={submitting || !displayName.trim()}>
          {submitting ? strings.create.submitting : strings.create.submit}
        </button>
      </form>
      <footer className="footer">{strings.footer.conceptPrototype}</footer>
    </div>
  );
}
